import { Box, Flex } from '@chakra-ui/react';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { Button, useToast } from '@morressier/alembic';
import {
  CustomFormFieldDefinition,
  FormFieldFileUploadArray
} from '@publishing-engine/domain/types/article';
import { JSONSchemaType } from 'ajv';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useWithdrawSubmission } from 'src/api/commands';
import {
  CustomFormFieldComponent,
  FormFieldMultipleFileUpload
} from 'src/components/FormField';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { IDecisionModalProps } from '../decisionModalHelper';
import { createEditorialModal } from '../EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from '../EditorialNote';
import StepHeader from '../../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';

import ComposeDecisionEmail, {
  EmailContentForm
} from '../ComposeDecisionEmail';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { Overlay } from '../../../Overlay';
import { buildEmailPayload } from '../../../../utils/normalizeEmailRecipients';
import { useSteps } from '../hooks';
import { useAttachmentSync } from '../../../../hooks/useAttachmentSync';
import NotifyReviewersToggle from '../NotifyReviewersToggle';
import DecisionLayout from '../DecisionLayout';
import ResponseToAuthorField from '../ResponseToAuthorField';
import { useEffect, useState } from 'react';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import ModerateCOCComment from '../ModerateCOCComment';
import RecommendationRounds from '../RecommendationRounds';
import { useQueryClient } from '@tanstack/react-query';

export const EditorialWithdrawSubmissionModal = createEditorialModal(
  ({
    onClose,
    submissionId,
    submissionContextId,
    organizationId
  }: IDecisionModalProps) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [notifyReviewers, setNotifyReviewers] = useState(false);
    const [notifyReviewerIds, setNotifyReviewerIds] = useState<string[]>([]);
    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'EditorialWithdrawSubmission'
    });

    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );
    const { recommedationSummary } = useCOCContextData();
    const {
      workflowResponse: {
        forms: { editorialWithdrawSubmission },
        articleWorkflow: { id: articleWorkflowId }
      },
      refetchSubmission,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const { formFields, schema } = editorialWithdrawSubmission || {};
    const fields = Object.values(formFields || {}).reduce(
      (acc, val) => ({
        ...acc,
        [val.name]: val as CustomFormFieldDefinition
      }),
      {} as Record<string, CustomFormFieldDefinition>
    );

    const methods = useForm({
      resolver: ajvResolver(schema as JSONSchemaType<unknown>)
    });

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    useEffect(() => {
      queryClient.invalidateQueries({
        queryKey: ['reviews-summary', submissionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendation-summary', submissionId]
      });
    }, []);
    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);

    const withdrawSubmissionMutation = useWithdrawSubmission({
      submissionId,
      organizationId,
      submissionContextId,
      articleWorkflowId,
      invalidateSubmissionQueries: refetchSubmission,
      emails: buildEmailPayload(emailContent),
      notifyReviewerIds
    });

    const withdrawalReason = methods.watch('withdrawalReason') as string | undefined;

    const withdrawSubmission = async (formValues: any) => {
      const latestRecommendationCycle =
        recommedationSummary?.recommendationRounds?.at(-1)?.cycle?.at(-1);
      const COCcomments = latestRecommendationCycle?.participants
        .map(
          item =>
            (item?.recommendation?.moderatedCommentForAuthor ?? '') ||
            (item?.recommendation?.commentForAuthor ?? '')
        )
        .filter(Boolean)
        .join(' \n');
      const editorComment = COCcomments
        ? `${formValues?.editorComment ?? ''}\n${COCcomments}`
        : formValues?.editorComment ?? '';
      const updatedFormValues = {
        ...formValues,
        editorComment: editorComment
      };

      const res = await withdrawSubmissionMutation.mutateAsync(
        updatedFormValues
      );
      if (res.isSuccess) {
        await submitEditorNote(editorNote);
        toast({
          variant: 'success',
          title: 'Submission Withdrawn',
          description:
            'The author has been notified via email about this decision'
        });
        onClose();
      } else {
        toast({
          variant: 'error',
          title: 'Error withdrawing submission',
          description: 'There was an error withdrawing the submission.'
        });
      }
    };

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        methods.handleSubmit(withdrawSubmission)();
      }
    };

    const onSubmitEmailStep = () => {
      methods.handleSubmit(withdrawSubmission)();
    };

    const renderFirstStep = () => (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitFirstStep)}>
          <Box marginX="auto">
            <StepHeader
              currentStep={1}
              totalSteps={hasCustomizableTemplates ? 2 : 0}
              heading="Withdraw Submission?"
              description="Proceeding with this decision confirms you have reviewed the
                  submission and wish to remove it from the publication process.
                  The author will be informed of this decision along with the
                  following information:"
              marginY={2}
            />
          </Box>{' '}
          <Box paddingX={1} paddingBottom={8}>
            <Flex flexDirection="column" gap={6}>
              {fields.WithdrawalReason && (
                <CustomFormFieldComponent
                  config={fields.WithdrawalReason}
                  placeholder="Select a reason for this withdrawal"
                />
              )}

              {fields.EditorComment && (
                <>
                  <ResponseToAuthorField
                    submissionId={submissionId}
                    decisionType="EditorialWithdrawSubmission"
                    subtypeSelected={withdrawalReason}
                  />
                  <CustomFormFieldComponent
                    config={fields.EditorComment}
                    placeholder="Add feedback to the author"
                  />
                </>
              )}

              {fields.Attachments && (
                <Box marginTop={-6}>
                  <FormFieldMultipleFileUpload
                    submissionId={submissionId}
                    submissionContextId={submissionContextId}
                    config={fields.Attachments as FormFieldFileUploadArray}
                  />
                </Box>
              )}
              {recommedationSummary?.recommendationRounds && (
                <ModerateCOCComment
                  RecommendationRound={
                    recommedationSummary?.recommendationRounds
                  }
                />
              )}
              <EditorialNote value={editorNote} onChange={setEditorNote} />
            </Flex>
          </Box>
        </form>
      </FormProvider>
    );

    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form onSubmit={emailContentMethods.handleSubmit(onSubmitEmailStep)}>
          <Box marginX="auto">
            <StepHeader
              currentStep={2}
              totalSteps={2}
              heading="Send Email Notification"
              description="Compose an email to notify the author about the withdrawal."
              marginY={2}
            />
          </Box>
          <ComposeDecisionEmail
            organizationId={organizationId}
            submissionContextId={submissionContextId}
            commandName="EditorialWithdrawSubmission"
            formPayload={methods.getValues()}
            subtype={withdrawalReason}
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderDecisionSidebar = () => <RecommendationRounds fullWidth />;

    const renderReviewerNotification = () => (
      <NotifyReviewersToggle
        submissionId={submissionId}
        organizationId={organizationId}
        submissionContextId={submissionContextId}
        commandName="EditorialWithdrawSubmission"
        isEnabled={notifyReviewers}
        selectedReviewerIds={notifyReviewerIds}
        onChange={(enabled, reviewerIds) => {
          setNotifyReviewers(enabled);
          setNotifyReviewerIds(reviewerIds);
        }}
      />
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(withdrawSubmission)()}
                isLoading={
                  !hasCustomizableTemplates &&
                  withdrawSubmissionMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() => emailContentMethods.handleSubmit(onSubmitEmailStep)()}
            isLoading={withdrawSubmissionMutation.isPending}
          >
            {'Submit Decision'}
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout
          manuscriptId={manuscriptId}
          onClose={onClose}
          sidebar={currentStep === 1 ? renderDecisionSidebar() : undefined}
        >
          <Box marginTop={2}>
            {currentStep === 1
              ? renderFirstStep()
              : hasCustomizableTemplates
              ? renderEmailStep()
              : null}
            {renderReviewerNotification()}
          </Box>
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
